import { priceFactory, type Price } from "@@/shared/prices/price";
import { z } from "zod";
import { subtractFromPrice } from "../prices/subtract-from-price";
import {
    bogoDiscountZodObject,
    type BogoDiscount,
} from "./discount-types/bogo/bogo-discount";
import {
    percentageDiscountZodObject,
    type PercentageDiscount,
} from "./discount-types/percentage/percentage-discount";
import {
    staggerDiscountZodObject,
    type StaggerDiscount,
} from "./discount-types/stagger/stagger-discount";
import type {
    TimePriceStepDiscount} from "./discount-types/time-price-step/time-price-step-discount";
import {
    timePriceStepDiscountZodObject,
} from "./discount-types/time-price-step/time-price-step-discount";
import {
    timeStepResourceDiscountZodObject,
    type TimeStepResourceDiscount,
} from "./discount-types/time-step-resource/time-step-resource-discount";
import {
    timeStepDiscountZodObject,
    type TimeStepDiscount,
} from "./discount-types/time-step/time-step-discount";
import {
    valueDiscountZodObject,
    type ValueDiscount,
} from "./discount-types/value/value-discount";

type Discount =
    | BogoDiscount
    | PercentageDiscount
    | StaggerDiscount
    | TimePriceStepDiscount
    | TimeStepDiscount
    | TimeStepResourceDiscount
    | ValueDiscount;

const discountZodSchema: z.ZodSchema<Discount> = z.discriminatedUnion("type", [
    bogoDiscountZodObject,
    percentageDiscountZodObject,
    staggerDiscountZodObject,
    timePriceStepDiscountZodObject,
    timeStepDiscountZodObject,
    timeStepResourceDiscountZodObject,
    valueDiscountZodObject,
]);
type DiscountType = Discount["type"];

const discountTypeZodSchema: z.ZodSchema<DiscountType> = z.enum([
    "BOGO_DISCOUNT",
    "PERCENTAGE_DISCOUNT",
    "STAGGER_DISCOUNT",
    "VALUE_DISCOUNT",
    "TIME_STEP_RESOURCE_DISCOUNT",
    "TIME_STEP_DISCOUNT",
    "TIME_PRICE_STEP_DISCOUNT",
]);

const _calculateDiscountedPrice = (discounts: Discount[]) => (price: Price) => {
    return discounts.reduce((result, discount) => {
        if (discount.type !== "PERCENTAGE_DISCOUNT")
            throw new Error("Rabatt-typen stöds inte; " + discount.type);
        result = subtractFromPrice(result, [
            priceFactory({
                amountIncludingVat: result.amountIncludingVat * discount.value,
            }),
        ]);
        return result;
    }, price);
};

const _calculateUndiscountedPrice =
    (discounts: Discount[]) => (price: Price) => {
        return discounts.reduce((result, discount) => {
            if (discount.type !== "PERCENTAGE_DISCOUNT")
                throw new Error("Rabatt-typen stöds inte; " + discount.type);
            result = priceFactory({
                amountIncludingVat:
                    result.amountIncludingVat / (1 - discount.value),
            });
            return result;
        }, price);
    };

export { discountTypeZodSchema, discountZodSchema };
export type { Discount, DiscountType };
